import { Assets, Loader } from "pixi.js";
import { PromiseCompletionSource } from "../shared/async/PromiseCompletionSource";
import { Event } from "../shared/event-system/Event";
import { IResourcePackage } from "./IResourcePackage";

export class DynamicResourcesPackage implements IResourcePackage {
    private readonly registrations: Array<string>;
    private resources: Record<string, any>;
    private loader: Loader;
    private alreadyLoading: PromiseCompletionSource<void>;
    private used: boolean;
    private loadComplete: boolean;
    public get loaded(): boolean {
        return this.loadComplete;
    }
    public readonly onLoadingProgress: Event<number>;

    constructor() {
        this.registrations = new Array();
        this.onLoadingProgress = new Event<number>();
        this.loader = Assets.loader;
        this.used = false;
        this.loadComplete = false;
    }

    public add(path: string): void {
        if (this.used) {
            throw new Error("Dynamic bundle already loaded.");
        }
        this.registrations.push(path);
    }

    public async load(): Promise<DynamicResourcesPackage> {
        if (this.registrations.length == 0) {
            throw new Error("Empty bundle cant be loaded.");
        }
        if (this.loaded) {
            return this;
        }
        try {
            if (this.alreadyLoading != undefined) {
                await this.alreadyLoading.result;
            } else {
                this.alreadyLoading = new PromiseCompletionSource<void>();
                this.resources = await this.loader.load(this.registrations, (progress: number) => this.onLoadingProgress.emit(progress));
                this.alreadyLoading.complete();
                this.loadComplete = true;
            }
        }
        catch (e: unknown) {
            console.error("Unable to load bundle, ", e);
        }
        finally {
            this.used = true;
            return this;
        }
    }

    public resource<T>(name: string): T {
        if (!this.loaded) {
            throw new Error(`Package ${this.constructor.name} not yet loaded.`);
        }
        return this.resources[name];
    }

    public destroy(): void {
        this.alreadyLoading.result.then(() => {
            this.loader.unload(this.registrations);
        });
    }
}
